import React, { useEffect, useState } from 'react';
import { Button, Card, InputNumber, List, Modal, Progress, Space, Tag, Tooltip, Typography, message } from 'antd';
import { PlusOutlined } from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import apiClient from '../services/apiClient';
import { userPath } from '../config/paths';
import { formatMoney } from '../utils/money';
import { useTranslation } from '../i18n';

const { Text } = Typography;

const fmtBytes = (v) => {
    const n = Number(v) || 0;
    if (n >= 1024 ** 4) return `${(n / 1024 ** 4).toFixed(2)} TB`;
    if (n >= 1024 ** 3) return `${(n / 1024 ** 3).toFixed(2)} GB`;
    if (n >= 1024 ** 2) return `${(n / 1024 ** 2).toFixed(1)} MB`;
    return `${n}`;
};
const fmtVal = (key, v) => (key.includes('bytes') ? fmtBytes(v) : Number(v).toLocaleString());

/**
 * 附加资源包 + 本周期用量（挂在某个订阅下）。
 * 购买出账单、付款后生效；周期包随主套餐续费，一次性包只在当前用量周期内有效。
 */
const AddonPanel = ({ subscriptionId, onChanged }) => {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const [data, setData] = useState(null);
    const [busy, setBusy] = useState(false);

    const load = async () => {
        if (!subscriptionId) return;
        try {
            const r = await apiClient.get('/api/user/subscription-info/addons', { params: { subscription_id: subscriptionId } });
            if (r.data?.success) setData(r.data.data);
        } catch (_) { /* 无权限/不存在时静默 */ }
    };
    useEffect(() => { setData(null); load(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [subscriptionId]);

    if (!subscriptionId || !data || (!data.available.length && !data.owned.length && !data.usage.length)) return null;

    const buy = (a) => {
        let qty = 1;
        Modal.confirm({
            title: t('Buy {{name}}', { name: a.name }),
            content: (
                <Space direction="vertical" size={6} style={{ width: '100%' }}>
                    {a.description && <Text type="secondary">{a.description}</Text>}
                    <span>
                        {t('Quantity: ')}
                        <InputNumber min={1} max={10} defaultValue={1} size="small" onChange={(v) => { qty = v || 1; }} />
                    </span>
                    <span>{t('Due now: ')}<b>{formatMoney(a.cost, a.currency)}</b>{qty > 1 ? ` × ${qty}` : ''}</span>
                    <Text type="secondary" style={{ fontSize: 12 }}>
                        {a.kind === 'pack' ? t('One-time pack, valid for the current usage period; does not renew.') : t('Prorated for the remaining days, then renews with your plan.')}
                    </Text>
                </Space>
            ),
            okText: t('Create invoice'),
            cancelText: t('Cancel'),
            onOk: async () => {
                setBusy(true);
                try {
                    const r = await apiClient.post('/api/user/subscription-info/addons', { subscription_id: subscriptionId, product_id: a.product_id, quantity: qty });
                    const d = r.data || {};
                    if (d.success && d.data?.invoice_id) {
                        message.success(d.data.existing ? t('An unpaid invoice already exists') : t('Invoice created; takes effect after payment'));
                        navigate(userPath(`/billing/invoices/${d.data.invoice_id}`));
                        onChanged && onChanged();
                    } else {
                        message.error(d.error || t('Failed to place order'));
                    }
                } catch (e) {
                    message.error(e?.response?.data?.error || e.message || t('Failed to place order'));
                } finally {
                    setBusy(false);
                }
            },
        });
    };

    return (
        <Card size="small" title={t('Add-ons')} style={{ marginBottom: 16 }}>
            {data.usage.length > 0 && (
                <List
                    size="small"
                    dataSource={data.usage}
                    renderItem={(u) => {
                        const pct = u.unlimited ? 0 : Math.min(100, Math.round((u.used / Math.max(1, u.limit)) * 100));
                        return (
                            <List.Item style={{ padding: '6px 0', border: 'none' }}>
                                <Space style={{ width: '100%', justifyContent: 'space-between' }} wrap>
                                    <Text type="secondary" style={{ minWidth: 90 }}>{t(u.label)}</Text>
                                    <Progress percent={pct} size="small" style={{ flex: 1, minWidth: 160, margin: 0 }}
                                        status={!u.unlimited && u.used > u.limit ? 'exception' : undefined}
                                        format={() => `${fmtVal(u.feature_key, u.used)} / ${u.unlimited ? t('Unlimited') : fmtVal(u.feature_key, u.limit)}`} />
                                    {u.overage_mode === 'charge' ? (
                                        <Tooltip title={t('Overage {{price}}/{{unit}}, settled at period end', { price: formatMoney(u.overage_price.unit_price, u.overage_price.currency), unit: fmtBytes(u.overage_price.unit) })}>
                                            <Tag color={u.estimated_charge > 0 ? 'orange' : 'default'} style={{ margin: 0 }}>
                                                {u.estimated_charge > 0 ? t('Overage {{amount}}', { amount: formatMoney(u.estimated_charge, u.overage_price.currency) }) : t('Pay-as-you-go overage')}
                                            </Tag>
                                        </Tooltip>
                                    ) : (
                                        <Tag style={{ margin: 0 }}>{t('Suspends when exceeded')}</Tag>
                                    )}
                                </Space>
                            </List.Item>
                        );
                    }}
                />
            )}
            {data.owned.length > 0 && (
                <Space wrap style={{ marginBlock: 8 }}>
                    {data.owned.map((o) => (
                        <Tag key={o.subscription_id} color={o.status === 'active' ? 'blue' : 'orange'} style={{ margin: 0 }}>
                            {o.name}{o.quantity > 1 ? ` × ${o.quantity}` : ''}
                            {o.status === 'pending' && o.unpaid_invoice_id && (
                                <Button type="link" size="small" style={{ padding: '0 0 0 4px', height: 'auto' }}
                                    onClick={() => navigate(userPath(`/billing/invoices/${o.unpaid_invoice_id}`))}>{t('Pay now')}</Button>
                            )}
                        </Tag>
                    ))}
                </Space>
            )}
            <Space wrap>
                {data.available.map((a) => (
                    <Button key={a.product_id} size="small" icon={<PlusOutlined />} loading={busy} onClick={() => buy(a)}>
                        {a.name} · {formatMoney(a.cost, a.currency)}{a.kind === 'recurring' ? t(' and up') : ''}
                    </Button>
                ))}
            </Space>
        </Card>
    );
};

export default AddonPanel;
